import { useEffect, useRef, useState } from "react";
import { useClerk } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { useAppAuth } from "@/lib/useAppAuth";

type Purchase = {
  paid: true;
  amount: number;
  currency: string;
  interval: "month" | "year";
};

type VerificationState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "verified"; purchase: Purchase };

type ClaimState = "idle" | "claiming" | "error";

function readError(payload: unknown, fallback: string): string {
  if (
    payload &&
    typeof payload === "object" &&
    "error" in payload &&
    typeof payload.error === "string"
  ) {
    return payload.error;
  }
  return fallback;
}

function formatAmount(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.toUpperCase(),
      maximumFractionDigits: 2,
    }).format(amount / 100);
  } catch {
    return `${currency.toUpperCase()} ${(amount / 100).toFixed(2)}`;
  }
}

export default function PurchaseCompletePage() {
  const { signOut } = useClerk();
  const { isLoaded, isSignedIn, userId } = useAppAuth();
  const queryClient = useQueryClient();
  const sessionId = new URLSearchParams(window.location.search).get("session_id");
  const returnTo = `${window.location.pathname}${window.location.search}`;
  const redirectUrl = encodeURIComponent(returnTo);
  const [verification, setVerification] = useState<VerificationState>({
    status: "loading",
  });
  const [claimState, setClaimState] = useState<ClaimState>("idle");
  const [claimError, setClaimError] = useState("");
  const [retryVersion, setRetryVersion] = useState(0);
  const [verificationRetry, setVerificationRetry] = useState(0);
  const attemptedClaimRef = useRef<string | null>(null);

  useEffect(() => {
    if (!sessionId) {
      setVerification({
        status: "error",
        message: "This purchase link is missing its checkout session. Please return to checkout or contact support.",
      });
      return;
    }

    const controller = new AbortController();
    let current = true;
    setVerification({ status: "loading" });
    setClaimState("idle");
    setClaimError("");
    attemptedClaimRef.current = null;

    void (async () => {
      try {
        const response = await fetch(
          `/api/billing/purchase?session_id=${encodeURIComponent(sessionId)}`,
          { credentials: "same-origin", signal: controller.signal },
        );
        const payload: unknown = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(readError(payload, "We couldn't confirm this payment. Please try again."));
        }
        if (
          !payload ||
          typeof payload !== "object" ||
          !("paid" in payload) ||
          payload.paid !== true ||
          !("amount" in payload) ||
          typeof payload.amount !== "number" ||
          !("currency" in payload) ||
          typeof payload.currency !== "string" ||
          !("interval" in payload) ||
          (payload.interval !== "month" && payload.interval !== "year")
        ) {
          throw new Error("We couldn't confirm a completed payment for this session.");
        }
        if (current) {
          setVerification({
            status: "verified",
            purchase: payload as Purchase,
          });
        }
      } catch (error) {
        if (current && !controller.signal.aborted) {
          setVerification({
            status: "error",
            message: error instanceof Error
              ? error.message
              : "We couldn't confirm this payment. Please try again.",
          });
        }
      }
    })();

    return () => {
      current = false;
      controller.abort();
    };
  }, [sessionId, verificationRetry]);

  useEffect(() => {
    if (
      verification.status !== "verified" ||
      !sessionId ||
      !isLoaded ||
      !isSignedIn ||
      !userId
    ) {
      return;
    }

    const attemptKey = `${sessionId}:${userId}`;
    if (attemptedClaimRef.current === attemptKey) return;
    attemptedClaimRef.current = attemptKey;

    const controller = new AbortController();
    let current = true;
    setClaimState("claiming");
    setClaimError("");

    void (async () => {
      try {
        const response = await fetch("/api/billing/purchase/claim", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId }),
          signal: controller.signal,
        });
        const payload: unknown = await response.json().catch(() => null);
        if (!response.ok || !payload || typeof payload !== "object" || !("ok" in payload) || payload.ok !== true) {
          throw new Error(readError(payload, "We couldn't connect this purchase to your account."));
        }
        if (!current) return;
        await queryClient.invalidateQueries();
        if (current) window.location.replace("/onboarding");
      } catch (error) {
        if (current && !controller.signal.aborted) {
          setClaimError(
            error instanceof Error
              ? error.message
              : "We couldn't connect this purchase to your account.",
          );
          setClaimState("error");
        }
      }
    })();

    return () => {
      current = false;
      controller.abort();
      if (attemptedClaimRef.current === attemptKey) attemptedClaimRef.current = null;
    };
  }, [verification, sessionId, isLoaded, isSignedIn, userId, queryClient, retryVersion]);

  const retryClaim = () => {
    attemptedClaimRef.current = null;
    setClaimState("idle");
    setClaimError("");
    setRetryVersion((version) => version + 1);
  };

  const processing =
    verification.status === "loading" ||
    (verification.status === "verified" &&
      (!isLoaded || (isSignedIn && claimState !== "error")));
  const signedInClaimError =
    verification.status === "verified" && isLoaded && isSignedIn && claimState === "error";

  return (
    <main className="flex min-h-[100dvh] w-full items-center justify-center bg-background px-4 py-10">
      <section
        className="w-full max-w-lg rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-9"
        aria-live="polite"
        data-testid="purchase-complete-card"
      >
        <div className="mb-7 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          {verification.status === "verified" && !signedInClaimError ? (
            <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
          ) : (
            <ShieldCheck className="h-6 w-6" aria-hidden="true" />
          )}
        </div>

        <p className="ds-label text-muted-foreground">INCONTROL MEMBERSHIP</p>
        <h1 className="mt-2 text-[25px] font-semibold tracking-tight text-foreground sm:text-[29px]">
          {verification.status === "verified"
            ? signedInClaimError
              ? "Payment confirmed"
              : isSignedIn
                ? "Setting up your account"
                : "Your payment is confirmed"
            : verification.status === "error"
              ? "We need to check something"
              : "Confirming your purchase"}
        </h1>

        {processing && (
          <div
            className="mt-6 flex items-center gap-3 text-[15px] text-muted-foreground"
            data-testid="purchase-processing"
          >
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            <span>
              {verification.status === "loading"
                ? "Confirming your payment…"
                : !isLoaded
                  ? "Checking your account…"
                  : "Connecting your purchase…"}
            </span>
          </div>
        )}

        {verification.status === "verified" && (
          <div className="mt-5">
            <p className="ds-body text-muted-foreground">
              We’ve confirmed your {verification.purchase.interval === "month" ? "monthly" : "annual"} InControl subscription.
            </p>
            <div className="mt-5 flex items-center justify-between gap-4 rounded-lg border border-border bg-background px-4 py-3">
              <span className="ds-label text-muted-foreground">Payment</span>
              <span className="text-[15px] font-semibold text-foreground">
                {formatAmount(verification.purchase.amount, verification.purchase.currency)}
                <span className="font-normal text-muted-foreground">
                  {" "}/ {verification.purchase.interval === "month" ? "month" : "year"}
                </span>
              </span>
            </div>
          </div>
        )}

        {verification.status === "error" && (
          <div className="mt-5 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3">
            <p className="ds-body text-destructive" data-testid="purchase-error">
              {verification.message}
            </p>
            {sessionId && (
              <button
                type="button"
                onClick={() => {
                  setVerification({ status: "loading" });
                  setVerificationRetry((version) => version + 1);
                }}
                className="mt-3 text-[14px] font-medium text-foreground underline underline-offset-4"
              >
                Try again
              </button>
            )}
          </div>
        )}

        {signedInClaimError && (
          <div className="mt-5 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3">
            <p className="ds-body text-destructive" data-testid="claim-error">{claimError}</p>
            <button
              type="button"
              onClick={retryClaim}
              className="ds-btn-secondary mt-4"
              data-testid="button-retry-claim"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => void signOut({ redirectUrl: `${window.location.pathname}${window.location.search}` })}
              className="ds-btn-secondary mt-4 ml-3"
              data-testid="button-switch-account"
            >
              Use a different account
            </button>
          </div>
        )}

        {verification.status === "verified" && isLoaded && !isSignedIn && (
          <div className="mt-7 border-t border-border pt-6">
            <p className="ds-body text-foreground">
              Create your InControl account to get started. Use the same email address you used at checkout so we can connect your purchase.
            </p>
            <a
              href={`/sign-up?redirect_url=${redirectUrl}`}
              className="ds-btn-primary mt-5 flex w-full justify-center"
              data-testid="button-create-account"
            >
              Create your account
            </a>
            <p className="mt-4 text-center text-[14px] text-muted-foreground">
              Already have an account?{" "}
              <a
                href={`/sign-in?redirect_url=${redirectUrl}`}
                className="font-medium text-foreground underline underline-offset-4"
                data-testid="link-sign-in"
              >
                Sign in
              </a>
            </p>
          </div>
        )}
      </section>
    </main>
  );
}
